import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CreateMealDialog } from "@/components/seller/CreateMealDialog";
import MealsGrid from "@/components/seller/meals/MealsGrid";
import MealsLoadingState from "@/components/seller/meals/MealsLoadingState";
import EmptyMealsState from "@/components/seller/meals/EmptyMealsState";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function SellerMeals() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: meals, isLoading } = useQuery({
    queryKey: ['seller-meals'],
    queryFn: async () => {
      const { data: userProfile } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('meals')
        .select('*')
        .eq('chef_id', userProfile.user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleDeleteMeal = async (id: string) => {
    try {
      const { error } = await supabase
        .from('meals')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['seller-meals'] });
      toast({
        title: "Success",
        description: "Meal deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast({
        title: "Error",
        description: "Failed to delete meal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEditMeal = (meal: any) => {
    // Navigate to edit page instead of opening dialog
    window.location.href = `/seller/meals/edit/${meal.id}`;
  };

  const handleToggleMealStatus = async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const { error } = await supabase
        .from('meals')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['seller-meals'] });
      toast({
        title: "Success",
        description: `Meal ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (error) {
      console.error('Error updating meal status:', error);
      toast({
        title: "Error",
        description: "Failed to update meal status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCreateMeal = async (mealData: any) => {
    try {
      console.log("Creating meal with data:", mealData);
      
      const { data: userProfile } = await supabase.auth.getUser();
      
      const insertData = {
        name: mealData.name,
        price: parseFloat(mealData.price),
        description: mealData.description,
        images: mealData.images,
        image: mealData.images[0] || null,
        chef_id: userProfile.user?.id,
        status: 'active',
        delivery_available: mealData.deliveryAvailable,
        delivery_fee: mealData.deliveryFee ? parseFloat(mealData.deliveryFee) : null,
        cuisine_type: mealData.cuisine_type,
        meal_type: mealData.meal_type,
        dietary_tags: mealData.dietary_tags,
        ingredients: mealData.ingredients,
        allergens: mealData.allergens,
        calories: mealData.calories ? parseInt(mealData.calories) : null,
        carbs: mealData.carbs ? parseFloat(mealData.carbs) : null,
        protein: mealData.protein ? parseFloat(mealData.protein) : null,
        fat: mealData.fat ? parseFloat(mealData.fat) : null,
        has_limited_portions: mealData.hasLimitedPortions,
        available_portions: mealData.hasLimitedPortions && mealData.available_portions ? parseInt(mealData.available_portions) : null,
        preparation_type: mealData.preparation_type,
        prep_time_hours: mealData.preparation_type === "on_demand" ? parseInt(mealData.prep_time_hours) || null : null,
        cutoff_time: mealData.preparation_type === "on_demand" ? mealData.cutoff_time : null,
        max_daily_orders: mealData.preparation_type === "on_demand" ? parseInt(mealData.max_daily_orders) || null : null,
        pickup_window_start: mealData.preparation_type === "on_demand" ? mealData.pickup_window_start : null,
        pickup_window_end: mealData.preparation_type === "on_demand" ? mealData.pickup_window_end : null,
        available_days: mealData.preparation_type === "on_demand" ? mealData.available_days : [],
        pickup_instructions: mealData.pickup_instructions || null,
        availability_slots: mealData.preparation_type === "scheduled" ? mealData.availabilitySlots : [],
        portion_size_value: mealData.portion_size_value ? parseFloat(mealData.portion_size_value) : null,
        portion_size_unit: mealData.portion_size_unit || null,
        // Custom pickup address fields
        use_custom_pickup_address: mealData.use_custom_pickup_address || false,
        pickup_address: mealData.use_custom_pickup_address ? mealData.pickup_address : null,
        pickup_city: mealData.use_custom_pickup_address ? mealData.pickup_city : null,
        pickup_state: mealData.use_custom_pickup_address ? mealData.pickup_state : null,
        pickup_postal_code: mealData.use_custom_pickup_address ? mealData.pickup_postal_code : null
      };

      console.log("Final insert data:", insertData);

      const { error } = await supabase
        .from('meals')
        .insert(insertData);

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ['seller-meals'] });
      
      toast({
        title: "Success",
        description: "Meal created successfully",
      });
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error('Error creating meal:', error);
      toast({
        title: "Error",
        description: "Failed to create meal. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <MealsLoadingState />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-black">My Meals</h1>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Meal
        </Button>
      </div>

      {meals && meals.length > 0 ? (
        <MealsGrid 
          meals={meals}
          onDeleteMeal={handleDeleteMeal}
          onEditMeal={handleEditMeal}
          onToggleMealStatus={handleToggleMealStatus}
        />
      ) : (
        <EmptyMealsState onCreate={() => setIsCreateDialogOpen(true)} />
      )}

      <CreateMealDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateMeal}
      />
    </div>
  );
}
